import {
  type QueryClient,
  useIsMutating,
  useMutation,
  useQuery,
  useQueryClient
} from "@tanstack/react-query";
import { toast } from "sonner";

import {
  AddEngagementDocument,
  AdminUsersDocument,
  ConsoleAccessLogDocument,
  CreateAdminUserDocument,
  CreateIdentityDocument,
  CreateDepartmentDocument,
  DepartmentsDocument,
  UpdateDepartmentDocument,
  SetDepartmentArchivedDocument,
  CreatePartnerOrganizationDocument,
  DeactivateInOktaDocument,
  DeleteEngagementDocument,
  DeletePartnerOrganizationDocument,
  ExportIdentityToOktaDocument,
  IdentitiesDocument,
  IdentityAuditLogDocument,
  IdentityDocument,
  IdentityOktaRequestsDocument,
  ImportRunRowsDocument,
  ImportRunsDocument,
  LookupsDocument,
  OktaAccessDocument,
  OktaSyncDocument,
  PartnerDrisDocument,
  PartnerOrganizationPeopleDocument,
  PartnerOrganizationsDocument,
  RestoreSystemAccessDocument,
  ReviveEngagementDocument,
  RunImportDocument,
  RunOktaExportDocument,
  SetAdminUserRolesDocument,
  SetAdminUserStatusDocument,
  SetEngagementBeamDetailDocument,
  SetEngagementEndDateDocument,
  SetEngagementStartDateDocument,
  SetEngagementTerminationDateDocument,
  SetPartnerOrganizationArchivedDocument,
  TerminateEngagementDocument,
  UpdateEngagementDocument,
  UpdateIdentityDocument,
  UpdatePartnerOrganizationDocument,
  type AddEngagementInput,
  type AdminUserStatus,
  type AdminUsersQuery,
  type CreateIdentityInput,
  type CreatePartnerOrganizationInput,
  type EngagementBeamDetailInput,
  type IdentityFilter,
  type EngagementAttributesInput,
  type ImportRunRowOutcome,
  type RunImportInput,
  type TerminateEngagementInput,
  type UpdatePartnerOrganizationInput,
  type Role,
  type UpdateIdentityInput
} from "src/graphql/generated";
import { request } from "src/api/client";
import { useBusinessDate } from "src/lib/use-business-date";

// Query keys. Grouped so a mutation can invalidate a whole area without listing
// every filter combination it might have populated.
export const keys = {
  viewer: ["viewer"] as const,
  lookups: ["lookups"] as const,
  partnerDris: ["partner-dris"] as const,
  identities: (filter?: IdentityFilter) => ["identities", filter ?? {}] as const,
  identitiesAll: ["identities"] as const,
  identity: (id: string) => ["identity", id] as const,
  // The prefix every per-person detail key starts with, for invalidating all of
  // them at once without knowing the ids.
  identityAny: ["identity"] as const,
  audit: (id: string) => ["audit", id] as const,
  // The prefix every per-person history key starts with. The single-person mutations
  // know whose history they changed and invalidate that one; a bulk import writes
  // audit rows for people it does not name back, so it invalidates all of them.
  auditAny: ["audit"] as const,
  adminUsers: ["admin-users"] as const,
  // The console-access history, which every admin_user write appends to.
  accessLog: ["access-log"] as const,
  departments: ["departments"] as const,
  partnerOrgs: ["partner-orgs"] as const,
  partnerOrgPeople: (orgId: string) => ["partner-org-people", orgId] as const,
  // The prefix every per-organization roster key starts with, so a write that moves
  // somebody between firms can invalidate all of them without knowing the ids.
  partnerOrgPeopleAny: ["partner-org-people"] as const,
  importRuns: ["import-runs"] as const,
  importRunRows: (runId: string) => ["import-run-rows", runId] as const,
  oktaAccess: (identityIds: string[]) => ["okta-access", identityIds] as const,
  oktaAccessAny: ["okta-access"] as const,
  oktaSync: (identityIds: string[]) => ["okta-sync", identityIds] as const,
  oktaSyncAny: ["okta-sync"] as const,
  // Under oktaSyncAny, so everything that refreshes the sync status refreshes this.
  oktaRequests: (identityId: string) => ["okta-sync", "requests", identityId] as const,
  /**
   * The key the import mutation itself carries.
   *
   * A mutation key rather than a bare hook, because the answer has to outlive the
   * screen. An import is synchronous and slow, and leaving the Bulk import tab
   * unmounts the component holding `isPending` — so a form that locked on that alone
   * would come back unlocked with the run still in flight, and a second click would
   * start a second import of the same sheet. `useImportRunning` asks the mutation
   * cache, which the tab change does not touch.
   */
  runImportMutation: ["run-import"] as const
};

export function useIdentities(filter: IdentityFilter) {
  // The date is part of the key and not part of the request. Status, hasLapsed and
  // filter membership are all derived from the server's calendar day, so the answer
  // expires at midnight with no mutation behind it; keying on the day is what makes
  // the boundary invalidate the cache.
  const today = useBusinessDate();
  return useQuery({
    queryKey: [...keys.identities(filter), today],
    queryFn: () => request(IdentitiesDocument, { filter })
  });
}

export function useIdentity(id: string | undefined) {
  const today = useBusinessDate();
  return useQuery({
    queryKey: [...keys.identity(id ?? ""), today],
    queryFn: () => request(IdentityDocument, { id: id as string }),
    enabled: Boolean(id)
  });
}

/**
 * Each listed person's Okta account, read from Okta when asked.
 *
 * Its own query rather than a field on the listing, so a slow or refusing Okta
 * leaves the list itself readable. Keyed on the ids, so paging or filtering asks
 * about whoever is now on screen.
 */
export function useOktaAccess(identityIds: string[], enabled: boolean) {
  return useQuery({
    queryKey: keys.oktaAccess(identityIds),
    queryFn: () => request(OktaAccessDocument, { identityIds }),
    enabled: enabled && identityIds.length > 0
  });
}

/**
 * How long after something is sent to Okta its access is read again.
 *
 * Okta's user search trails its writes, so somebody sent a moment ago can read as
 * not in Okta, or in their old status, for a while. A few reads spread over two
 * minutes catch that up. Polling instead would search Okta for everyone on screen
 * all day, against a rate limit every integration in the Okta org shares.
 */
export const OKTA_ACCESS_RECHECKS_MS = [15_000, 45_000, 120_000] as const;

const oktaAccessRechecks = new WeakMap<QueryClient, ReturnType<typeof setTimeout>[]>();

/**
 * Reads Okta access again now and at each of OKTA_ACCESS_RECHECKS_MS, after a write
 * that may have sent to Okta. A later write starts the schedule over rather than
 * adding to it.
 */
export function recheckOktaAccess(queryClient: QueryClient) {
  const refresh = () => queryClient.invalidateQueries({ queryKey: keys.oktaAccessAny });
  refresh();
  for (const timer of oktaAccessRechecks.get(queryClient) ?? []) {
    clearTimeout(timer);
  }
  oktaAccessRechecks.set(
    queryClient,
    OKTA_ACCESS_RECHECKS_MS.map((ms) => setTimeout(refresh, ms))
  );
}

/**
 * Whether Okta holds the latest of each person's record, from this service's own
 * record of what it sent rather than from Okta. Every edit changes the answer, so
 * every identity mutation and export invalidates it.
 */
export function useOktaSync(identityIds: string[], enabled: boolean) {
  return useQuery({
    queryKey: keys.oktaSync(identityIds),
    queryFn: () => request(OktaSyncDocument, { identityIds }),
    enabled: enabled && identityIds.length > 0
  });
}

/**
 * The requests sent to Okta that named one person, newest first, asking for one more
 * than `shown` so the screen can tell whether older ones were left out.
 */
export function useIdentityOktaRequests(identityId: string, shown: number, enabled: boolean) {
  return useQuery({
    queryKey: keys.oktaRequests(identityId),
    queryFn: () => request(IdentityOktaRequestsDocument, { identityId, limit: shown + 1 }),
    enabled
  });
}

export function useIdentityAudit(id: string | undefined) {
  return useQuery({
    queryKey: keys.audit(id ?? ""),
    queryFn: () => request(IdentityAuditLogDocument, { identityId: id as string }),
    enabled: Boolean(id)
  });
}

export function useLookups() {
  return useQuery({
    queryKey: keys.lookups,
    queryFn: () => request(LookupsDocument),
    // Entities and subcategories change when a migration adds one, not during a
    // session.
    staleTime: 10 * 60 * 1000
  });
}

export function usePartnerDris(enabled = true) {
  return useQuery({
    queryKey: keys.partnerDris,
    queryFn: () => request(PartnerDrisDocument),
    enabled,
    staleTime: 10 * 60 * 1000
  });
}

export function usePartnerOrgs() {
  // The date is in the key for the same reason it is on useIdentities, and it did
  // not have to be until INFRA-650. This listing's usage number used to be a plain
  // count of engagement rows, which no clock could change; the headcounts that
  // replaced it derive from where each term sits relative to the server's calendar
  // day, so a term that lapses overnight moves a person from current to ended with
  // no mutation behind it. Nothing else would notice: mutations invalidate,
  // refetchOnWindowFocus is off, and an elapsed staleTime does not refetch a query
  // that stays mounted.
  //
  // The mutations still invalidate with the bare keys.partnerOrgs prefix, which
  // matches this longer key — the same arrangement identities uses.
  const today = useBusinessDate();
  return useQuery({
    queryKey: [...keys.partnerOrgs, today],
    queryFn: () => request(PartnerOrganizationsDocument)
  });
}

/**
 * The people engaged through one organization, fetched when a row is expanded.
 *
 * What keeps it off the initial render is that the panel holding it is not rendered
 * until then, and that matters: the Organizations screen is the whole table, so a
 * roster per row would be exactly the query-per-row the headcount was made a single
 * aggregate to avoid — and the payload would be every partner in the database rather
 * than a column of numbers. `enabled` is the belt to that braces, matching
 * useIdentity: a caller that has no id yet asks for nothing rather than for "".
 *
 * Keyed on the business date for the same reason the identity queries are: a term's
 * status, and so which group somebody falls in, is derived from the server's
 * calendar day, so the answer expires at midnight with no mutation behind it.
 */
export function useOrgPeople(orgId: string | null, limit: number) {
  const today = useBusinessDate();
  return useQuery({
    queryKey: [...keys.partnerOrgPeople(orgId ?? ""), limit, today],
    queryFn: () =>
      request(PartnerOrganizationPeopleDocument, { organizationId: orgId as string, limit }),
    enabled: Boolean(orgId)
  });
}

export function useAdminUsers(enabled: boolean) {
  return useQuery({
    queryKey: keys.adminUsers,
    queryFn: () => request(AdminUsersDocument),
    enabled
  });
}

/**
 * The history of console access, for the panel under the user list.
 *
 * No `enabled` flag, unlike useAdminUsers beside it, and the difference is
 * structural rather than an oversight. That one is called before its screen's
 * iam-admin check, because hooks cannot be called conditionally, so it needs a flag
 * to keep from firing. This one lives in a child component the screen renders only
 * after that check, so a non-iam-admin never mounts it and there is nothing for a
 * flag to switch off. Neither is the access control: `unscopedAuditLog` requires
 * PermReadUnscopedAudit of whoever asks.
 *
 * `limit` is in the key, because it is the caller's page size and a different one is
 * a different answer.
 *
 * The three overrides below are the only ones in this file, and they are here because
 * this panel makes a claim no other read makes: that it is showing the *complete*
 * history. The app-wide defaults in main.tsx are `refetchOnWindowFocus: false` and a
 * 30-second `staleTime`, and TanStack does not refetch a mounted query when its
 * staleTime lapses — only on a trigger. So without these a panel left open goes on
 * asserting completeness against a read that could be an hour old, refreshed only by
 * a write made in this same browser. Another administrator granting access in another
 * session would never appear.
 *
 * Focus and reconnect rather than a polling interval: the review workflow is to read
 * the screen, act, and come back to it, and a timer on an idle tab would poll an
 * audit log forever to cover a case a focus event already covers. `staleTime: 0` is
 * what makes either trigger actually refetch.
 *
 * Freshness is only half of it — a trigger can always be one moment behind. The panel
 * also states the time of the read it is describing, from `dataUpdatedAt`, so the
 * claim is true of a stated moment rather than implied to be true of now.
 */
export function useConsoleAccessLog(limit: number) {
  return useQuery({
    queryKey: [...keys.accessLog, limit],
    queryFn: () => request(ConsoleAccessLogDocument, { limit }),
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true
  });
}

/**
 * Every bulk import ever run, newest first.
 *
 * `limit` is in the key for the same reason it is on the access log: it is the
 * caller's page size, and a different one is a different answer.
 *
 * No business date in the key, unlike the identity reads. Nothing here is derived
 * from the server's calendar day — a run's counts, status and timestamps are what it
 * did, and midnight does not change any of them.
 *
 * `enabled` for the same reason useAdminUsers takes one: the screen calls this above
 * its own iam-admin check, because hooks cannot be called conditionally, and
 * `importRuns` needs read_unscoped_audit. Without the flag every non-iam-admin who
 * reached the screen would fire a read the server exists to refuse.
 */
export function useImportRuns(limit: number, enabled: boolean) {
  return useQuery({
    queryKey: [...keys.importRuns, limit],
    queryFn: () => request(ImportRunsDocument, { limit }),
    enabled
  });
}

/**
 * One page of one run's per-row report, fetched when a run is selected.
 *
 * `enabled` matches useIdentity and useOrgPeople: nothing is selected until an
 * operator runs an import or picks a run out of the history, and a caller with no id
 * yet asks for nothing rather than for "". That matters more here than elsewhere,
 * because `importRunRows` errors on an unknown run rather than answering an empty
 * list.
 *
 * `outcomes` and `offset` are part of the key for the reason `limit` is: each is a
 * different question, and a cached answer to one must not be served for another. A
 * paged report cached under one key would show page 1's rows while page 2 loaded.
 */
export function useImportRunRows(
  runId: string | null,
  outcomes: ImportRunRowOutcome[],
  notedOnly: boolean,
  limit: number,
  offset: number
) {
  return useQuery({
    queryKey: [...keys.importRunRows(runId ?? ""), outcomes, notedOnly, limit, offset],
    queryFn: () =>
      request(ImportRunRowsDocument, {
        importRunId: runId as string,
        outcomes,
        notedOnly,
        limit,
        offset
      }),
    enabled: Boolean(runId)
  });
}

/**
 * Whether an import is running anywhere in this browser tab.
 *
 * Read from the mutation cache rather than from the mutation hook, so the answer
 * survives the Bulk import screen unmounting — see `keys.runImportMutation`.
 */
export function useImportRunning(): boolean {
  return useIsMutating({ mutationKey: keys.runImportMutation }) > 0;
}

/**
 * Push every current person to the configured Okta identity source.
 *
 * Everyone active who never had system access is given it first, and anyone sent
 * without a work email is given one, each an edit and an audit entry on the person —
 * so every listing, person and history is re-read, on settle because a run that
 * failed part-way may already have saved some.
 */
export function useRunOktaExport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => request(RunOktaExportDocument),
    onError: (err: Error) => toast.error(err.message),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: keys.identitiesAll });
      queryClient.invalidateQueries({ queryKey: keys.identityAny });
      queryClient.invalidateQueries({ queryKey: keys.auditAny });
      recheckOktaAccess(queryClient);
      queryClient.invalidateQueries({ queryKey: keys.oktaSyncAny });
    }
  });
}

/**
 * Push one person to Okta from their page.
 *
 * A person without a work email is given one before they are sent, so their record
 * and history are re-read, on settle because the address is kept even when Okta
 * then refuses them. The toast carries Okta's request id, which is how the request
 * is found in Okta's System Log.
 */
export function useExportIdentityToOkta() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (identityId: string) => request(ExportIdentityToOktaDocument, { identityId }),
    onSuccess: (data) => {
      const requestId = data.exportIdentityToOkta.oktaRequestId;
      toast.success(requestId ? `Sent to Okta (request ${requestId})` : "Sent to Okta");
    },
    onError: (err: Error) => toast.error(err.message),
    onSettled: (_data, _err, identityId) => {
      queryClient.invalidateQueries({ queryKey: keys.identitiesAll });
      queryClient.invalidateQueries({ queryKey: keys.identity(identityId) });
      queryClient.invalidateQueries({ queryKey: keys.audit(identityId) });
      recheckOktaAccess(queryClient);
      queryClient.invalidateQueries({ queryKey: keys.oktaSyncAny });
    }
  });
}

/**
 * Running one import.
 *
 * The success toast says which of the two happened, because "Import finished" over a
 * dry run is the one sentence this feature must never produce: it describes the mode
 * that writes, about the mode that does not.
 *
 * A dry run invalidates nothing but the run listing, and that asymmetry is the point
 * of it. A real run creates people and terms — so the identity listings, every
 * person's detail and audit history, and the organization lookups, headcounts and
 * rosters all have to be re-read. A dry run rolls its writes back, so invalidating
 * those would send the console to re-read data that has not changed.
 */
export function useRunImport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: keys.runImportMutation,
    mutationFn: (input: RunImportInput) => request(RunImportDocument, { input }),
    onError: (err: Error) => toast.error(err.message),
    onSuccess: (_result, input) =>
      toast.success(input.dryRun ? "Dry run finished — nothing was written" : "Import finished"),
    onSettled: (_data, _err, input) => {
      // On settle rather than on success, and for both modes: a run that failed
      // part-way still has a record, and on a real run still leaves committed
      // whatever it wrote before it stopped.
      queryClient.invalidateQueries({ queryKey: keys.importRuns });
      if (input.dryRun) {
        return;
      }
      queryClient.invalidateQueries({ queryKey: keys.identitiesAll });
      queryClient.invalidateQueries({ queryKey: keys.identityAny });
      // Including a person who was already here: an attached row is a new term on
      // them, which is an audit entry under a history this console caches per person.
      queryClient.invalidateQueries({ queryKey: keys.auditAny });
      queryClient.invalidateQueries({ queryKey: keys.oktaSyncAny });
      // Imports can create organizations. History labels and form pickers resolve
      // their names through this lookup, which otherwise stays fresh for ten minutes.
      queryClient.invalidateQueries({ queryKey: keys.lookups });
      queryClient.invalidateQueries({ queryKey: keys.partnerOrgs });
      queryClient.invalidateQueries({ queryKey: keys.partnerOrgPeopleAny });
    }
  });
}

/**
 * Who tells the operator that a write succeeded.
 *
 * `quiet` hands that job to the caller, and exists for one shape: a form that sends
 * more than one of these mutations for a single button press. Each hook announcing
 * itself turns one action into two toasts, and a half-failure into a green
 * "saved" followed by a red error — two halves of a sentence arriving out of order,
 * with the misleading half first. The form that knows what it sent says it once.
 *
 * Failure is never quiet. An error toast is the only report a caller gets for free,
 * and suppressing it would be how a refused write becomes a silent one.
 */
type MutationNotice = { quiet?: boolean };

/**
 * Every mutation below invalidates the identity area and the record's own audit
 * history, because every mutation writes an audit row — a stale history is the
 * most confusing thing this console could show.
 */
function useIdentityMutation<TInput, TResult>(
  run: (input: TInput) => Promise<TResult>,
  // A function of the input for a mutation whose wording depends on what the caller
  // knew when it sent it: giving system access is a grant or a restore.
  successMessage: string | ((input: TInput) => string),
  identityIdOf: (input: TInput) => string | undefined,
  options?: MutationNotice
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: run,
    onError: (err: Error) => toast.error(err.message),
    onSuccess: (_result, input) => {
      if (!options?.quiet) {
        toast.success(
          typeof successMessage === "function" ? successMessage(input) : successMessage
        );
      }
    },
    onSettled: (_data, _err, input) => {
      queryClient.invalidateQueries({ queryKey: keys.identitiesAll });
      // Any of these can attach an engagement to an organization, which changes the
      // headcount the Organizations screen shows and the delete button it offers.
      // Without this a cached listing keeps its stale zero and offers a delete the
      // server then refuses — the enabled-then-error the count exists to prevent.
      queryClient.invalidateQueries({ queryKey: keys.partnerOrgs });
      // And the rosters behind those counts, for the same reason: ending somebody's
      // term moves them from the current group to the ended one, and an expanded
      // panel showing them as current would contradict the number above it.
      queryClient.invalidateQueries({ queryKey: keys.partnerOrgPeopleAny });
      // Every one of these is a change Okta may need to hear about, and those that
      // reach a current person send it.
      queryClient.invalidateQueries({ queryKey: keys.oktaSyncAny });
      recheckOktaAccess(queryClient);
      const id = identityIdOf(input);
      if (id) {
        queryClient.invalidateQueries({ queryKey: keys.identity(id) });
        queryClient.invalidateQueries({ queryKey: keys.audit(id) });
      }
    }
  });
}

export function useCreateIdentity() {
  return useIdentityMutation(
    (input: CreateIdentityInput) => request(CreateIdentityDocument, { input }),
    "Person created",
    () => undefined
  );
}

export function useUpdateIdentity() {
  return useIdentityMutation(
    (input: UpdateIdentityInput) => request(UpdateIdentityDocument, { input }),
    "Saved",
    (input) => input.id
  );
}

export function useTerminateEngagement() {
  return useIdentityMutation(
    (input: TerminateEngagementInput) => request(TerminateEngagementDocument, { input }),
    "Engagement ended",
    (input) => input.identityId
  );
}

/**
 * Removing somebody's system access apart from when their contracts end, taking them
 * out of Okta in the same request. A removal belongs to the person, whichever terms
 * keep them in Okta.
 */
export function useDeactivateInOkta() {
  return useIdentityMutation(
    (identityId: string) => request(DeactivateInOktaDocument, { identityId }),
    "System access removed",
    (identityId) => identityId
  );
}

/**
 * Giving system access: back after a removal, or for the first time to somebody with
 * no removal on record (`granting`). Either exports them to Okta when Okta export is
 * configured. Nothing else gives it.
 */
export function useRestoreSystemAccess() {
  return useIdentityMutation(
    ({ identityId }: { identityId: string; granting: boolean }) =>
      request(RestoreSystemAccessDocument, { identityId }),
    ({ granting }) => (granting ? "System access granted" : "System access restored"),
    ({ identityId }) => identityId
  );
}

export function useAddEngagement() {
  return useIdentityMutation(
    (input: AddEngagementInput) => request(AddEngagementDocument, { input }),
    "Engagement added",
    (input) => input.identityId
  );
}

/**
 * Correcting what a term is: its entity, subcategory, firm or title.
 *
 * Keyed on the engagement, so the identity id rides along purely to invalidate that
 * person's caches — the same arrangement as beam detail below.
 *
 * An omitted field is left alone and an empty string clears one, so the caller sends
 * the form's values through as they are rather than filtering the blanks out. That
 * matters for `partnerOrganizationId`: the create forms write
 * `partnerOrganizationId || undefined`, and copying that idiom here would turn
 * "clear the firm" into "leave it alone" and make clearing impossible.
 */
export function useUpdateEngagement(options?: MutationNotice) {
  return useIdentityMutation(
    (args: { engagementId: string; identityId: string; fields: EngagementAttributesInput }) =>
      request(UpdateEngagementDocument, {
        input: { engagementId: args.engagementId, fields: args.fields }
      }),
    "Engagement saved",
    (args) => args.identityId,
    options
  );
}

/**
 * Beam detail is keyed on the engagement, so the identity id rides along purely to
 * invalidate that person's caches — the same arrangement as reviving below.
 */
export function useSetEngagementBeamDetail(options?: MutationNotice) {
  return useIdentityMutation(
    (args: { engagementId: string; identityId: string; detail: EngagementBeamDetailInput }) =>
      request(SetEngagementBeamDetailDocument, {
        engagementId: args.engagementId,
        detail: args.detail
      }),
    "Beam detail saved",
    (args) => args.identityId,
    options
  );
}

/**
 * Setting the day a term is contracted to end. Distinct from ending it: this says
 * when the term is due to run out, and the term stays active until the day arrives.
 *
 * `endDate: null` clears it and makes the term open-ended again.
 */
export function useSetEngagementEndDate() {
  return useIdentityMutation(
    (args: { engagementId: string; identityId: string; endDate: string | null }) =>
      request(SetEngagementEndDateDocument, {
        engagementId: args.engagementId,
        endDate: args.endDate
      }),
    "Contracted end saved",
    (args) => args.identityId
  );
}

/**
 * Record, correct, or clear the first day of a term.
 *
 * `startDate: null` clears the recorded date. The server derives the status from
 * all three dates, so this can change which status filter returns the person.
 */
export function useSetEngagementStartDate() {
  return useIdentityMutation(
    (args: { engagementId: string; identityId: string; startDate: string | null }) =>
      request(SetEngagementStartDateDocument, {
        engagementId: args.engagementId,
        startDate: args.startDate
      }),
    "Start date saved",
    (args) => args.identityId
  );
}

/**
 * Reviving takes an engagement id, so the identity id has to be passed alongside it
 * purely to invalidate that person's caches — the mutation itself does not need it.
 */
export function useReviveEngagement() {
  return useIdentityMutation(
    (args: { engagementId: string; identityId: string }) =>
      request(ReviveEngagementDocument, { engagementId: args.engagementId }),
    "Engagement revived",
    (args) => args.identityId
  );
}

/**
 * Removing a term that should never have been recorded — a duplicate, a typo, a row
 * an import filed against the wrong person.
 *
 * Takes the engagement id like the revive above, with the identity id alongside it
 * purely to invalidate that person's caches. The success message says "removed"
 * rather than "deleted" because that is the word the dialog and the button use, and
 * an operator who has just pressed one should be told the thing they pressed
 * happened.
 *
 * No optimistic update and nothing written into the cache: the mutation returns an
 * id, the refusals are the server's, and the invalidation below re-reads the person
 * — whose engagement list is the thing that changed.
 */
export function useDeleteEngagement() {
  return useIdentityMutation(
    (args: { engagementId: string; identityId: string }) =>
      request(DeleteEngagementDocument, { engagementId: args.engagementId }),
    "Engagement removed",
    (args) => args.identityId
  );
}

/**
 * Correcting the day a term was ended, on a term that was ended.
 *
 * `terminatedOn` is a required string, not `string | null` like the two dates above:
 * clearing this one returns the term to current, which is `useReviveEngagement`, and
 * the schema says `Date!` so a null cannot be sent at all.
 */
export function useSetEngagementTerminationDate() {
  return useIdentityMutation(
    (args: { engagementId: string; identityId: string; terminatedOn: string }) =>
      request(SetEngagementTerminationDateDocument, {
        engagementId: args.engagementId,
        terminatedOn: args.terminatedOn
      }),
    "Last day corrected",
    (args) => args.identityId
  );
}

function useAdminMutation<TArgs, TResult>(
  run: (args: TArgs) => Promise<TResult>,
  successMessage: string,
  // Each of these mutations returns the row it just wrote. Writing that into the
  // cache here, rather than waiting for the refetch, is what stops the next toggle
  // from building its set out of a stale snapshot: setAdminUserRoles replaces the
  // whole role set, so a lost update is a silently dropped grant or revocation.
  updated?: (result: TResult) => AdminUsersQuery["adminUsers"][number]
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: run,
    onError: (err: Error) => toast.error(err.message),
    onSuccess: (result) => {
      const user = updated?.(result);
      if (user) {
        queryClient.setQueryData<AdminUsersQuery>(keys.adminUsers, (prev) =>
          prev
            ? { ...prev, adminUsers: prev.adminUsers.map((u) => (u.id === user.id ? user : u)) }
            : prev
        );
      }
      toast.success(successMessage);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: keys.adminUsers });
      // A role change can alter what the caller themselves may see.
      queryClient.invalidateQueries({ queryKey: keys.viewer });
      // Every one of these writes an audit entry, and the history sits on the same
      // screen directly below the control that wrote it. Without this a grant appears
      // in the user list and not in the history beside it, which reads as the history
      // being incomplete — the exact doubt the panel exists to remove. Invalidated on
      // settle rather than on success because a refused write is worth re-reading too:
      // it may have been refused for a reason the log explains.
      queryClient.invalidateQueries({ queryKey: keys.accessLog });
    }
  });
}

export function useCreateAdminUser() {
  return useAdminMutation(
    (args: { email: string; roles: Role[] }) => request(CreateAdminUserDocument, args),
    "Console access granted"
  );
}

export function useSetAdminUserRoles() {
  return useAdminMutation(
    (args: { id: string; roles: Role[] }) => request(SetAdminUserRolesDocument, args),
    "Roles updated",
    (result) => result.setAdminUserRoles
  );
}

export function useSetAdminUserStatus() {
  return useAdminMutation(
    (args: { id: string; status: AdminUserStatus }) => request(SetAdminUserStatusDocument, args),
    "Status updated",
    (result) => result.setAdminUserStatus
  );
}

/**
 * Both organization mutations invalidate the lookup cache as well as the listing,
 * because the pickers on the create and detail screens are fed from it — and
 * `useLookups` holds its data for ten minutes. Without this, an organization
 * created inline would not appear in the dropdown that the person creating it is
 * about to use, which is the whole point of the inline form.
 */
function usePartnerOrgMutation<TArgs, TResult>(
  run: (args: TArgs) => Promise<TResult>,
  // A function of the arguments, not just a string, for the one mutation whose
  // message depends on which way it was called: archiving and restoring go through
  // the same call and "Organization saved" would describe neither.
  successMessage: string | ((args: TArgs) => string)
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: run,
    onError: (err: Error) => toast.error(err.message),
    onSuccess: (_result, args) =>
      toast.success(typeof successMessage === "function" ? successMessage(args) : successMessage),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: keys.partnerOrgs });
      queryClient.invalidateQueries({ queryKey: keys.lookups });
      // Every engagement row embeds its organization's name, so a rename leaves the
      // old one on the people list and on each person's detail until those refetch
      // on their own. Renaming is the main reason to edit one of these at all.
      queryClient.invalidateQueries({ queryKey: keys.identitiesAll });
      queryClient.invalidateQueries({ queryKey: keys.identityAny });
    }
  });
}

export function useCreatePartnerOrg() {
  return usePartnerOrgMutation(
    (input: CreatePartnerOrganizationInput) =>
      request(CreatePartnerOrganizationDocument, { input }),
    "Organization added"
  );
}

export function useUpdatePartnerOrg() {
  return usePartnerOrgMutation(
    (input: UpdatePartnerOrganizationInput) =>
      request(UpdatePartnerOrganizationDocument, { input }),
    "Organization saved"
  );
}

/**
 * Archiving invalidates the same four areas as the others, and the lookups one is
 * the load-bearing invalidation here: it is what removes the organization from the
 * create and add-engagement pickers, which is the only thing archiving changes.
 *
 * The toast says which direction it went. "Saved" would be useless for an action
 * whose whole result is a row quietly leaving a dropdown.
 */
export function useSetPartnerOrgArchived() {
  return usePartnerOrgMutation(
    (args: { id: string; archived: boolean }) =>
      request(SetPartnerOrganizationArchivedDocument, args),
    (args) => (args.archived ? "Organization archived" : "Organization restored")
  );
}

/**
 * Deleting invalidates the same four areas as the others, and every one of them
 * earns its place even though the row is gone rather than changed: the listing loses
 * it, `lookups` feeds the pickers that were still offering it, and the identity
 * queries embed organization names — a firm deletable at all has no engagements, but
 * a list cached from before it was created is not a list that knows that.
 *
 * The toast names the action rather than saying "Saved". A deletion is the one
 * mutation here with nothing left on screen to show it happened.
 */
export function useDeletePartnerOrg() {
  return usePartnerOrgMutation(
    (args: { id: string }) => request(DeletePartnerOrganizationDocument, args),
    "Organization deleted"
  );
}

export function useDepartments() {
  return useQuery({
    queryKey: keys.departments,
    queryFn: () => request(DepartmentsDocument)
  });
}

function useDepartmentMutation<TInput, TResult>(
  run: (input: TInput) => Promise<TResult>,
  successMessage: string | ((input: TInput) => string)
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: run,
    onError: (err: Error) => toast.error(err.message),
    onSuccess: (_data, input) =>
      toast.success(typeof successMessage === "function" ? successMessage(input) : successMessage),
    onSettled: () => {
      // Pickers and historical references share the catalogue; person queries embed names.
      for (const queryKey of [
        keys.departments,
        keys.lookups,
        keys.identitiesAll,
        keys.identityAny
      ]) {
        queryClient.invalidateQueries({ queryKey });
      }
    }
  });
}

export function useCreateDepartment() {
  return useDepartmentMutation(
    (args: { name: string }) => request(CreateDepartmentDocument, args),
    "Department added"
  );
}

export function useUpdateDepartment() {
  return useDepartmentMutation(
    (args: { id: string; name: string }) => request(UpdateDepartmentDocument, args),
    "Department saved"
  );
}

export function useSetDepartmentArchived() {
  return useDepartmentMutation(
    (args: { id: string; archived: boolean }) => request(SetDepartmentArchivedDocument, args),
    (args) => (args.archived ? "Department archived" : "Department restored")
  );
}
